import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { VisitRow } from "./Dashboard";
import { toast } from "sonner";

const RANGES = [30, 90, 180, 365];

export default function JobHistory() {
  const [visits, setVisits] = useState(null);
  const [days, setDays] = useState(90);
  const [q, setQ] = useState("");
  const [prop, setProp] = useState("");

  useEffect(() => {
    setVisits(null);
    api.get(`/visits/history?days=${days}`)
      .then(({ data }) => setVisits(data))
      .catch((e) => { toast.error(fmtErr(e)); setVisits([]); });
  }, [days]);

  const props = useMemo(() => {
    const m = new Map();
    (visits || []).forEach((v) => v.property_id && m.set(v.property_id, v.property_name));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [visits]);

  const match = (v) => {
    if (prop && v.property_id !== prop) return false;
    const s = `${v.property_name} ${v.owner_name} ${v.address} ${v.assignee_name || ""} ${(v.services || []).map((x) => x.name).join(" ")}`.toLowerCase();
    return s.includes(q.toLowerCase());
  };

  const filtered = (visits || []).filter(match);
  const byMonth = filtered.reduce((m, v) => ((m[v.date.slice(0, 7)] = m[v.date.slice(0, 7)] || []).push(v), m), {});
  const monthLabel = (mk) => new Date(`${mk}-01T12:00:00`).toLocaleDateString("en-CA", { month: "long", year: "numeric" });

  return (
    <Layout title="Job history">
      <div data-testid="job-history-page">
        <input data-testid="history-search" className="field-input mb-4 w-full" placeholder="Search property, owner, address, crew…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="mb-4 flex flex-wrap gap-2" data-testid="history-ranges">
          {RANGES.map((d) => (
            <button key={d} data-testid={`history-range-${d}`} onClick={() => setDays(d)}
              className={`h-10 rounded-full border px-4 text-xs font-bold transition-colors ${days === d ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
              {d === 365 ? "1 year" : `${d} days`}
            </button>
          ))}
        </div>
        {props.length > 1 && (
          <select data-testid="history-property-filter" className="field-input mb-4 w-full" value={prop} onChange={(e) => setProp(e.target.value)}>
            <option value="">All properties</option>
            {props.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        {!visits && <p className="text-sm text-muted-foreground">Loading…</p>}
        {visits && (
          <p className="mb-4 text-sm text-muted-foreground" data-testid="history-count">
            {filtered.length} completed job{filtered.length === 1 ? "" : "s"} in the last {days === 365 ? "year" : `${days} days`}
          </p>
        )}
        {visits && filtered.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground" data-testid="history-empty">No completed jobs in this range.</p>}
        <div data-testid="history-list">
          {Object.entries(byMonth).map(([month, vs]) => (
            <section key={month} className="mb-8">
              <p className="label-caps mb-3" data-testid={`history-month-${month}`}>{monthLabel(month)}</p>
              <div className="space-y-2">{vs.map((v) => <VisitRow key={v.id} v={v} showDate />)}</div>
            </section>
          ))}
        </div>
      </div>
    </Layout>
  );
}
